import { DesktopDatePicker, DesktopTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import './CreateSchedule.scss';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import { Controller, FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { FC, ReactNode, useEffect, useState } from 'react';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CREATE_SESSION, DELETE_SCHEDULED_SESSION, UPDATE_SCHEDULED_SESSION } from '../../pages/LiveClass/api/LiveClassQuery';
import { useMutation } from '@apollo/client';
import { useAppSelector } from '../../redux/app/hooks';
import ToasterWithMessage from '../toaster';
import { singleSessionType } from '../../pages/LiveClass/LiveClass';
import ConfirmationPopup from '../ConfirmationPopup/ConfirmationPopup';
import images from '../../assets/images';
import { useNavigate } from 'react-router-dom';
import { JITSI, messages, patterns, STUDENT_ROLE } from '../../utils/constants';
import { clickedWorkshopDetails } from '../../pages/Workshop/Workshop';
import { DELETE_WORKSHOP, SCHEDULE_WORKSHOP, UPDATE_WORKSHOP } from '../../pages/Workshop/api/WorkshopQuery';

dayjs.extend(utc);
dayjs.extend(timezone);


interface IFormInput {
    lessonTopic: string;
    description: string;
    startTime: any;
    endTime: any;
    dateValue: any;
    timeRange?: string;
    teachingPeriod:any;
    discussionPeriod:any;
}

// Helper function to convert "HH:mm" to total minutes
const convertToMinutes = (period: string) => {
    const [hours, minutes] = period.split(':').map(Number);
    return hours * 60 + minutes;
};

// Define Yup schema
const schema = yup.object().shape({
    lessonTopic: yup
    .string()
    .trim()
    .required('Lessons topic is required')
    // .matches(patterns.alphabetAndNumber,messages.LessonsTopic)
    // .matches(patterns.NotStartWithNumber,messages.LessonsTopicNotStartNumber)
    // .min(5, 'Lessons topic must be between 5 and 30 characters long')
    .max(100, 'Lessons topic must be between 1 and 100 characters long'),
    description:yup
    .string()
    .trim()
    .required('Description is required')
    // .min(5,'Description must be between 5 and 150 characters long')
    .max(150, 'Description must be between 1 and 150 characters long'),
    dateValue: yup.date().required('Date is required'),
    startTime: yup
        .string()
        .required('Start time is required')
        // .uppercase()
        // .matches(/^(0[1-9]|1[0-2]):[0-5][0-9] [AP]M$/, 'Invalid time format or Hour must not be 0')
        .test('is-after-now', messages.meetingStartTime, function (value:any) {
          const { dateValue } = this.parent;
          if (!value || !dateValue) return true;
          const selectedDate = dayjs(dateValue);
          const currentTime = dayjs();
          const selectedTime = dayjs(value, 'hh:mm A');
          if (selectedDate.isSame(currentTime, 'day')) {
            const diffMinutes = selectedTime.diff(currentTime, 'minute');
            return diffMinutes >= 6;
          }
          return true;
        }),
    endTime: yup
        .string()
        .required('End time is required')
        .uppercase()
        // .matches(/^(0[1-9]|1[0-2]):[0-5][0-9] [AP]M$/, 'Invalid time format or Hour must not be 0')
        .test('is-after-now', 'End time must be in the future', function (value: any) {
            const { dateValue } = this.parent;
            if (!value || !dateValue) return true;
            const selectedDate = dayjs(dateValue);
            const currentTime = dayjs();
            const selectedTime = dayjs(value, 'hh:mm A');
            if (selectedDate.isSame(currentTime, 'day')) {
                return selectedTime.isAfter(currentTime);
            }
            return true;
        }),
    timeRange: yup.string().test('is-valid-time-range', 'End time must be later than start time', function (value, context) {
            const { startTime, endTime } = context.parent;
            if (!startTime || !endTime) {
                return true; // Skip this validation if either time is not set
            }
            const start = dayjs(startTime, 'hh:mm A');
            const end = dayjs(endTime, 'hh:mm A');
            return end.isAfter(start);
    }),
    teachingPeriod: yup
        .string()
        .required('Teaching period is required')
        .test('is-valid-period', 'Teaching period exceeds time range', function (value, context) {
            const { startTime, endTime, discussionPeriod } = context.parent;
            if (!startTime || !endTime) return true;
            const start = dayjs(startTime, 'hh:mm A');
            const end = dayjs(endTime, 'hh:mm A');
            const totalAvailableMinutes = end.diff(start, 'minute');
            const teachingMinutes = value ? convertToMinutes(value) : 0;
            const discussionMinutes = discussionPeriod ? convertToMinutes(discussionPeriod) : 0;
            return (teachingMinutes + discussionMinutes) <= totalAvailableMinutes;
        }),
    discussionPeriod: yup
        .string()
        .required('Discussion period is required')
        .test('is-valid-period', 'Discussion period exceeds time range', function (value, context) {
            const { startTime, endTime, teachingPeriod } = context.parent;
            if (!startTime || !endTime) return true;
            const start = dayjs(startTime, 'hh:mm A');
            const end = dayjs(endTime, 'hh:mm A');
            const totalAvailableMinutes = end.diff(start, 'minute');
            const discussionMinutes = value ? convertToMinutes(value) : 0;
            const teachingMinutes = teachingPeriod ? convertToMinutes(teachingPeriod) : 0;
            return (discussionMinutes + teachingMinutes) <= totalAvailableMinutes;
        }),
});


interface propsType{
    setShowCreateScheduling: (val:boolean) => void;
    clickedSessionDetails?: singleSessionType | clickedWorkshopDetails;
    component: string;
    setEditClicked?: (val: boolean) => void; 
    editClicked?: boolean;
}
const CreateSchedule: FC<propsType> = ({setShowCreateScheduling, editClicked, setEditClicked, clickedSessionDetails, component}) => {
    

    const methods = useForm<IFormInput>({
        resolver: yupResolver(schema),
    });
    const { DeleteGif, teacherDeleteIcon } = images;
    const { register, handleSubmit, control, trigger, reset, setValue, formState: { errors, isSubmitted } } = methods;
    const dateValue = useWatch({ control, name: 'dateValue' });
    const startTime = useWatch({ control, name: 'startTime' });
    const endTime = useWatch({ control, name: 'endTime' });
    const teachingPeriod = useWatch({ control, name: 'teachingPeriod' });
    const discussionPeriod = useWatch({ control, name: 'discussionPeriod' });
    const selectedClassRoomId = useAppSelector((state) => state.auth.selectedClassRoomId);
    const selectedSubjectId = localStorage.getItem('selectedSId');
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const { role, loginpage } = useAppSelector((state) => state.auth);
    const { error, success } = ToasterWithMessage();
    const navigate = useNavigate();
    const meetService = useAppSelector((state) => state.auth.meetService);

    // Define your custom styles
    const customStyle = {
        '& .MuiInputBase-root': {
            backgroundColor: 'white',
            width: '113px',
            height: '35px',
            outline: 'none',
            border: 'none',
            '&:focus': {
                outline: 'none',
                border: 'none'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
            },
            '& .MuiInputBase-input::placeholder': {
                opacity: 1,
                fontFamily: 'Inter',
                color: '#616161', 
                fontSize: '12px',
                fontWeight: 400,
                '&:focus': {
                fontSize: '12px',
                color: '#616161', 
            },
            },
            '& .MuiInputBase-input': {
                fontSize: '12px', 
            },
            '& .MuiInputAdornment-root': {
                marginLeft: '0px',
            },
            '& .MuiInputBase-input.Mui-disabled': {
                opacity: 1,
                WebkitTextFillColor: 'unset',
                color: '#616161'
            },
        },
    };
    
    const customPeriodStyle = {
        '& .MuiInputBase-root': {
            backgroundColor: 'white',
            width: '162px',
            height: '35px',
            outline: 'none',
            border: 'none',
            '&:focus': {
                outline: 'none',
                border: 'none'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
            },
            '& .MuiInputBase-input::placeholder': {
                opacity: 1,
                fontFamily: 'Inter',
                color: '#616161', 
                fontSize: '12px',
                fontWeight: 400,
                '&:focus': {
                fontSize: '12px',
                color: '#616161', 
            },
            },
            '& .MuiInputBase-input': {
                fontSize: '12px', 
            },
            '& .MuiInputAdornment-root': {
                marginLeft: '0px',
            },
            '& .MuiInputBase-input.Mui-disabled': {
                opacity: 1,
                WebkitTextFillColor: 'unset',
                color: '#616161'
            },
        },
    }

    // To schedule the workshop
    const [scheduleWorkshop,{loading:workShopLoading}] = useMutation(SCHEDULE_WORKSHOP, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            setShowCreateScheduling(false);
            success('Created successfully');
        },
        onError: (err) => {
            error(err?.message);
        }
    });
    
    //To update the workshop
    const [updateWorkshop,{loading:updateWorkshopLoading}] = useMutation(UPDATE_WORKSHOP, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            setShowCreateScheduling(false);
            if(setEditClicked){
                setEditClicked(false);
            }
            success("Updated successfully");
        },
        onError: (err) => {
            error(err?.message);
        }
    });

    //API for scheduling the meeting
    const [createLiveSession,{loading:sessionLoading}] = useMutation(CREATE_SESSION,{
        fetchPolicy:"no-cache",
        onCompleted:(data)=>{
           setShowCreateScheduling(false);
           success('Created successfully');
        },
        onError:(err)=>{
            error(err?.message);
            if (err?.message === "Subject not found for teacher" || err?.message === "Subject not found") {
                setTimeout(() => {
                    if (role === STUDENT_ROLE) {
                        navigate('/student-classroom');
                    } else {
                        navigate('/teacher-classroom');
                    }
                }, 2000);
            }
        }
      });

      const [editSession,{loading:updateSessionLoading}] = useMutation(UPDATE_SCHEDULED_SESSION, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
          setShowCreateScheduling(false);
          if(setEditClicked){
            setEditClicked(false);
          }
          success("Updated successfully");
        },
        onError: (err) => {
            error(err?.message);
            if (err?.message === "Subject not found for teacher" || err?.message === "Subject not found") {
                setTimeout(() => {
                    if (role === STUDENT_ROLE) {
                        navigate('/student-classroom');
                    } else {
                        navigate('/teacher-classroom');
                    }
                }, 2000);
            }
        }
    });

    const [deleteSession,{loading:deleteSessionLoading}] = useMutation(DELETE_SCHEDULED_SESSION, {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        setShowCreateScheduling(false);
        if(setEditClicked)
        setEditClicked(false);
        success("Deleted successfully");
      },
        onError: (err) => {
            error(err?.message);
            if (err?.message === "Subject not found for teacher" || err?.message === "Subject not found") {
                setTimeout(() => {
                    if (role === STUDENT_ROLE) {
                        navigate('/student-classroom');
                    } else {
                        navigate('/teacher-classroom');
                    }
                }, 2000);
            }
        }
    });


    const [deleteWorkshop,{loading:deleteWorkshopLoading}] = useMutation(DELETE_WORKSHOP, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            setShowCreateScheduling(false);
            if(setEditClicked)
            setEditClicked(false);
            success("Deleted successfully");
        },
        onError: (err) => {
            error(err?.message);
        }
    });

    useEffect(() => {        
        handleCalculateTime(dayjs(startTime, 'hh:mm A'), dayjs(endTime, 'hh:mm A'));
        if (startTime && endTime && isSubmitted) {
            trigger('timeRange'); // Trigger the timeRange validation
        }
    }, [startTime, endTime, trigger]); 

    useEffect(() => {
        if ((teachingPeriod || discussionPeriod) && isSubmitted) {
            trigger('teachingPeriod');
            trigger('discussionPeriod');
        }
    }, [startTime, endTime, teachingPeriod, discussionPeriod, trigger]); 

    useEffect(()=>{
      if(dateValue){
        trigger('discussionPeriod');
        trigger('teachingPeriod');
        trigger('endTime');
        trigger('startTime');
        trigger('timeRange');
      }
    },[dateValue,trigger])
    
const convertToUTC = (dateValue: string, timeValue:string) => {
    // Ensure dateValue is a string
    const dateStr = String(dateValue);

    // Extract date part from the dateValue string
    const datePart = dateStr.split(' ').slice(0, 4).join(' ');
    // Combine the date part with timeValue
    const combinedDateTime = `${datePart} ${timeValue}`;

    // Create a moment object from the combined date and time
    const localDate = moment(combinedDateTime, 'ddd MMM DD YYYY hh:mm A');
    // Get the local timezone
    const localTimezone = moment.tz.guess();

    // Convert the local date to UTC
    const utcDate = localDate.tz(localTimezone).utc();

    // Format the UTC date as required
    const formattedUTCDate = utcDate.utc().format();

    return formattedUTCDate;
    };

    const formatStartAndEndTime = (timing: string) => {
        return moment.utc(timing).local().format('hh:mm A');
    }

    const areAnyChanges = (data: IFormInput) => {
        let returnValue;
        if(clickedSessionDetails?.type === 'liveclass'){
            returnValue = (dayjs(data?.dateValue).format('YYYY-MM-DD') !== dayjs(clickedSessionDetails?.startdate).format('YYYY-MM-DD') ||
            data?.startTime !== (clickedSessionDetails?.startdate && formatStartAndEndTime(clickedSessionDetails?.startdate)) ||
            data?.endTime !== (clickedSessionDetails?.enddate && formatStartAndEndTime(clickedSessionDetails?.enddate)) ||
            data?.lessonTopic !== clickedSessionDetails?.lessonTopic || 
            data?.description !== clickedSessionDetails?.description ||
            data?.teachingPeriod !== clickedSessionDetails?.teachingPeriod ||
            data?.discussionPeriod !== clickedSessionDetails?.discussionPeriod)
                return returnValue;
        }else if(clickedSessionDetails?.type === 'workshop'){
            returnValue = (dayjs(data?.dateValue).format('YYYY-MM-DD') !== dayjs(clickedSessionDetails?.wsStartTime).format('YYYY-MM-DD') ||
            data?.startTime !== (clickedSessionDetails?.wsStartTime && formatStartAndEndTime(clickedSessionDetails?.wsStartTime)) ||
            data?.endTime !== (clickedSessionDetails?.wsEndTime && formatStartAndEndTime(clickedSessionDetails?.wsEndTime)) ||
            data?.lessonTopic !== clickedSessionDetails?.wsTopic || 
            data?.description !== clickedSessionDetails?.wsDescription ||
            data?.teachingPeriod !== clickedSessionDetails?.wsTeachingPeriod ||
            data?.discussionPeriod !== clickedSessionDetails?.wsInteractionsPeriod)
                return returnValue;
        }
    }

    const onSubmitForm: SubmitHandler<IFormInput> = (data) => {
            const startDate = convertToUTC(data.dateValue, data.startTime);
            const endDate = convertToUTC(data.dateValue, data.endTime);
            
            const inputVariables = {
                cId: selectedClassRoomId,
                sjId: selectedSubjectId,
                startDate: startDate,
                endDate: endDate,
                topic: data?.lessonTopic,
                discussionPeriod: data?.discussionPeriod,
                teachingPeriod: data?.teachingPeriod,
                description: data?.description
            };

            if(editClicked){
                if(clickedSessionDetails && areAnyChanges(data)){
                    if(component === 'liveclass'){
                        editSession({
                            variables: {
                              cId: selectedClassRoomId,
                              sjId: selectedSubjectId,
                              startDate: startDate,
                              endDate: endDate,
                              topic: data?.lessonTopic,
                              discussionPeriod: data?.discussionPeriod,
                              teachingPeriod: data?.teachingPeriod,
                              description: data?.description,
                              sId: clickedSessionDetails?.type === 'liveclass' && clickedSessionDetails?.sessionId,
                              ...(meetService === JITSI ? { pMeetingType: "JITSI" } : {}),
                            }
                          })
                    }else {
                        updateWorkshop({
                            variables: {
                                input: {
                                  workshopPatch: {
                                    wsTopic: data?.lessonTopic,
                                    wsEndTime: endDate,
                                    wsStartTime: startDate,
                                    wsTeachingPeriod: data?.teachingPeriod,
                                    wsInteractionsPeriod: data?.discussionPeriod,
                                    wsDescription: data?.description,
                                    ...(meetService === JITSI ? { meetingType: "JITSI" } : {}),
                                  },
                                  wsId: clickedSessionDetails?.type === 'workshop' && clickedSessionDetails?.wsId
                                }
                              }
                        })
                    }
                    
                }else {
                    if(!areAnyChanges(data)){
                        error("There is no changes to update");
                    }
                }
            }else {
                    if(component === 'liveclass'){
                        createLiveSession({
                            variables: { ...inputVariables, ...(meetService === JITSI ? { pMeetingType: "JITSI" } : {}) }
                        })
                    } else if(component === 'workshop'){
                        scheduleWorkshop({
                            variables: {
                                input: {
                                  workshop: {
                                    wsTopic: data?.lessonTopic,
                                    wsEndTime: endDate,
                                    wsStatus: "NOT_STARTED",
                                    wsStartTime: startDate,
                                    wsTeachingPeriod: data?.teachingPeriod,
                                    wsInteractionsPeriod: data?.discussionPeriod,
                                    wsDescription: data?.description,
                                    ...(meetService === JITSI ? { meetingType: "JITSI" } : {}),
                                  }
                                },
                                isFacilitator: false
                              }
                        })
                    }
            }
    }

    const onClickClearBtn = () => {
        reset({
            lessonTopic: "",
            startTime: "",
            endTime: "",
            dateValue: null,
            timeRange: "",
            discussionPeriod: "",
            teachingPeriod: "",
            description: ""
        })
    }

    const onClickDeleteBtn = () => {
        setOpenConfirmationPopup(true);
    }

    useEffect(() => {
        if (editClicked && clickedSessionDetails) {
            if(clickedSessionDetails?.type === 'liveclass' && component === "liveclass"){
                reset({
                    lessonTopic: clickedSessionDetails?.lessonTopic,
                    startTime: formatStartAndEndTime(clickedSessionDetails?.startdate),
                    endTime: formatStartAndEndTime(clickedSessionDetails?.enddate),
                    dateValue: dayjs(clickedSessionDetails?.startdate), // Setting the date value
                    discussionPeriod: clickedSessionDetails?.discussionPeriod,
                    teachingPeriod: clickedSessionDetails?.teachingPeriod,
                    description: clickedSessionDetails?.description
                })
            }else if(clickedSessionDetails?.type === 'workshop' && component === 'workshop'){
                reset({
                    lessonTopic: clickedSessionDetails?.wsTopic,
                    startTime: formatStartAndEndTime(clickedSessionDetails?.wsStartTime),
                    endTime: formatStartAndEndTime(clickedSessionDetails?.wsEndTime),
                    dateValue: dayjs(clickedSessionDetails?.wsStartTime), // Setting the date value
                    discussionPeriod: clickedSessionDetails?.wsInteractionsPeriod,
                    teachingPeriod: clickedSessionDetails?.wsTeachingPeriod,
                    description: clickedSessionDetails?.wsDescription
                })
            }
        }
    }, [clickedSessionDetails]);

    const onYesButtonClick = () => {
        setOpenConfirmationPopup(false);
        if(component === 'liveclass' && clickedSessionDetails?.type === 'liveclass'){
            deleteSession({
                variables: {
                  cId: selectedClassRoomId,
                  sjId: selectedSubjectId,
                  sId: clickedSessionDetails?.sessionId,
                  ...(meetService === JITSI ? { pMeetingType: "JITSI" } : {}),
                }
            })
        }else if(component === 'workshop' && clickedSessionDetails?.type === 'workshop'){
            deleteWorkshop({
                variables: {
                    input: {
                        wsId: clickedSessionDetails?.wsId
                    },
                    ...(meetService === JITSI ? { meetingType: "JITSI" } : {}),
                }
            });
        }
        
    }

    const onNoButtonClick = () => {
        setOpenConfirmationPopup(false);
    }

    useEffect(() => {
        if(!editClicked){
            const start = dayjs().add(7, 'minutes'); // Setting start time 7 minutes from now
            const end = dayjs().add(67, 'minutes');  // End time 60 minutes after the start time
            setValue('dateValue', dayjs(new Date()));
            setValue('startTime', start.format('hh:mm A'));
            setValue('endTime', end.format('hh:mm A'));
        }
    }, [editClicked])

    const formatDuration = (ms: any) => {
        const hours = Math.floor(ms / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}:${minutes}`;
    };

    const timeToMilliseconds = (time: any) => {
        const [hours, minutes] = time.split(':').map(Number);
        return (hours * 60 * 60 * 1000) + (minutes * 60 * 1000);
    }

    const handleCalculateTime = (start: any, end: any) => {
        if (!start?.isValid() || !end?.isValid()) {
            setValue('discussionPeriod', "");
            setValue('teachingPeriod', "");
            trigger('teachingPeriod');
            trigger('discussionPeriod');
            return;
        }
        const totalDurationMs = end?.diff(start, 'minutes');
        if (totalDurationMs < 0) {
            setValue('discussionPeriod', "00h:00m");
            setValue('teachingPeriod', "00h:00m")
            trigger('teachingPeriod');
            trigger('discussionPeriod');
            return;
        }
        const teachingDurationMs = Math.floor(totalDurationMs * 0.75) * 60 * 1000;
        const discussionDurationMs = Math.ceil(totalDurationMs * 0.25) * 60 * 1000;     
        setValue('discussionPeriod', formatDuration(discussionDurationMs));
        setValue('teachingPeriod', formatDuration(teachingDurationMs))
    }

    const handleChangeTime = (field: any, newValue: any, type: string) => {
        const start = dayjs(startTime, 'hh:mm A');
        const end = dayjs(endTime, 'hh:mm A');
        if (!start?.isValid() || !end?.isValid()|| !dayjs(newValue)?.isValid) {
            setValue('discussionPeriod', "");
            setValue('teachingPeriod', "");
            trigger('teachingPeriod');
            trigger('discussionPeriod');
            return;
        }
        const total = end?.diff(start, 'millisecond');
        const value = newValue?.format('HH:mm');
        field.onChange(value ? value : '');
        if (type === 'teachingPeriod') {
            total > timeToMilliseconds(value) ? setValue('discussionPeriod', formatDuration(total - timeToMilliseconds(value))) : setValue('discussionPeriod', '00:00') ;
            trigger('teachingPeriod');
            trigger('discussionPeriod');
        }
        else if (type === 'discussionPeriod') {
            total > timeToMilliseconds(value) ? setValue('teachingPeriod', formatDuration(total - timeToMilliseconds(value))) : setValue('teachingPeriod', '00:00');
            trigger('teachingPeriod');
            trigger('discussionPeriod');
        }
    };
   
  return (
    <>
        <div className='scheduleHeadingContainer'>{component === 'liveclass' ? "Schedule your Live Lessons." : "Schedule a Workshop."}</div>
        <div className='scheduleFormContainer'>
            <FormProvider {...methods}>
                <form className='formStyle' autoComplete="off" onSubmit={handleSubmit(onSubmitForm)}>
                      <div className='topContainer'>
                          <div className='topicAndDescriptionCtn'>
                              <div className='messageContainer'>
                                  <label className='textareaLabelStyle'>{component === 'liveclass' ? 'Lesson Topic' : 'Topic'}</label>
                                  <input
                                      {...register('lessonTopic')}
                                      id="input"
                                      placeholder={'Topic goes here...'}
                                      className='lessonTopicStyle'
                                  />
                                  {errors.lessonTopic && isSubmitted && <span className="error">{errors?.lessonTopic && errors?.lessonTopic?.message}</span>}
                              </div>
                              <div className='messageContainer'>
                                  <label className='textareaLabelStyle'>Description</label>
                                  <textarea
                                      {...register('description')}
                                      id="textareaBox"
                                      placeholder={'Messages goes here...'}
                                      className='textareaStyle'
                                  />
                                  {errors.description && isSubmitted && <span className="error">{errors?.description && errors?.description?.message}</span>}
                              </div>
                          </div>
                        <div className='dataTimeContainer'>
                                <div className='DateContainer'>
                                    <label className='dateLabelStyle'>Date</label>
                                    <Controller
                                        control={control}
                                        name={'dateValue'}
                                        render={({ field }) => (
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DemoContainer components={['DesktopDatePicker']} sx={{overflow: 'hidden', paddingTop: 0}}>
                                                <DesktopDatePicker
                                                disablePast
                                                {...field}
                                                value={field.value || null}
                                                onChange={(e, value) => {
                                                    field.onChange(e)
                                                }}
                                                format="DD/MM/YY"
                                                minDate={dayjs().startOf('year')}
                                                maxDate={dayjs().endOf('year')}
                                                slotProps={{
                                                    textField: {
                                                    onKeyDown: (e) => {e.preventDefault()},
                                                    placeholder: 'DD/MM/YY',
                                                    sx: {
                                                        '& .MuiInputBase-root': {
                                                    backgroundColor: 'white',
                                                    width: '162px',
                                                    height: '35px',
                                                    outline : 'none',
                                                    border: 'none',
                                                    '&:focus': {
                                                        outline: 'none',
                                                    },
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: 'none',
                                                    },
                                                    '& .MuiInputBase-input::placeholder': {
                                                        opacity: 1,
                                                        fontFamily: 'Inter',
                                                        color: '#616161', // Change placeholder color
                                                        fontSize: '12px', // Change placeholder font size,
                                                        fontWeight: 400,
                                                        '&:focus': {
                                                        fontSize: '12px',
                                                        color: '#616161', // Change placeholder color
                                                    },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '12px', // Set font size for entered values
                                                    },
                                                    },
                                                    },
                                                }}
                                                />
                                                </DemoContainer>
                                            </LocalizationProvider>
                                        )}
                                    />
                                    {errors.dateValue && isSubmitted && <span className="error">{errors.dateValue.message as ReactNode}</span>}
                                </div>
                                <div className='startTimeContainer'>
                                    <label className='dateLabelStyle'>Start Time</label>
                                    {/* <TimeTextField name={'startTime'}/> */}
                                    <Controller
                                        name="startTime"
                                        control={control}
                                        render={({ field }) => {
                                            // Defining the Threshold time as current time plus 7 minutes
                                            const threshold = dayjs().add(7, "minutes");
                                            // Checking if the selected date is today
                                            const isToday = dateValue && dayjs(dateValue).isSame(threshold, 'day');
                                            // Setting minTime to thresholdTime if the selected date is today
                                            const minTime = isToday ? threshold : undefined;

                                            return (
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DesktopTimePicker
                                                        sx={customStyle}
                                                        {...field}
                                                        value={field.value ? dayjs(field.value, 'hh:mm A') : null}
                                                        onChange={(newValue) => field.onChange(newValue ? newValue.format('hh:mm A') : '')}
                                                        format="hh:mm A"
                                                        timeSteps={{minutes:1}}
                                                        minTime={minTime}
                                                        slotProps={{
                                                            textField: {
                                                                placeholder: "00:00 AM",
                                                            }
                                                        }}
                                                        closeOnSelect={false}
                                                    />
                                                </LocalizationProvider>
                                            )
                                        }}
                                    />
                                    {errors.startTime && isSubmitted && <span className="error">{errors.startTime.message as ReactNode}</span>}
                                </div>
                                <div className='endTimeContainer'>
                                    <label className='dateLabelStyle'>End Time</label>
                                    {/* <TimeTextField name={"endTime"}/> */}
                                    <Controller
                                        name="endTime"
                                        control={control}
                                        render={({ field }) => (
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DesktopTimePicker
                                                    sx={customStyle}
                                                    {...field}
                                                    value={field.value ? dayjs(field.value, 'hh:mm A') : null}
                                                    onChange={(newValue) => field.onChange(newValue ? newValue.format('hh:mm A') : '')}
                                                    format="hh:mm A"
                                                    timeSteps={{minutes:1}}
                                                    slotProps={{
                                                        textField: {
                                                            placeholder: "00:00 AM",
                                                        }
                                                    }}
                                                    closeOnSelect={false}
                                                />
                                            </LocalizationProvider>
                                        )}
                                    />
                                    {errors.endTime && isSubmitted && <span className="error">{errors.endTime.message as ReactNode}</span>}
                                    {errors.timeRange && isSubmitted && (!errors.startTime && !errors.endTime) && <span className="error">{errors.timeRange.message}</span>}
                                </div>
                        </div>
                        {(component === 'liveclass') && (<div className='periodContainer'>
                                <div className='teachingPeriodContainer'>
                                    <label className='dateLabelStyle'>Teaching Period</label>
                                    <Controller
                                        name="teachingPeriod"
                                        control={control}
                                        render={({ field }) => (
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DesktopTimePicker
                                                    sx={customPeriodStyle}
                                                    {...field}
                                                    value={field.value ? dayjs(field.value, 'HH:mm') : null}
                                                    onChange={(newValue) => handleChangeTime(field, newValue, 'teachingPeriod')}
                                                    format="HH:mm"    
                                                    timeSteps={{minutes:1}}
                                                    ampm={false}                                                
                                                    slotProps={{
                                                        textField: {
                                                            placeholder: "hh:mm",
                                                        }
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        )}
                                    />
                                    {errors.teachingPeriod && isSubmitted && <span className="error">{errors.teachingPeriod.message as ReactNode}</span>}
                                </div>
                                <div className='teachingPeriodContainer'>
                                    <label className='dateLabelStyle'>Discussion Period</label>
                                    <Controller
                                        name="discussionPeriod"
                                        control={control}
                                        render={({ field }) => (
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DesktopTimePicker
                                                    sx={customPeriodStyle}
                                                    {...field}
                                                    value={field.value ? dayjs(field.value, 'HH:mm') : null}
                                                    onChange={(newValue) => handleChangeTime(field, newValue, 'discussionPeriod')}
                                                    format="HH:mm"
                                                    timeSteps={{minutes:1}}
                                                    ampm={false}
                                                    slotProps={{
                                                        textField: {
                                                            placeholder: "hh:mm",
                                                        }
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        )}
                                    />
                                    {errors.discussionPeriod && isSubmitted && <span className="error">{errors.discussionPeriod.message as ReactNode}</span>}
                                </div>
                                </div>)}
                    </div>
                    <div className='bottomContainer'>
                        {
                            clickedSessionDetails ?
                            <div className='clearButtonContainer' onClick={()=>{!(deleteSessionLoading||deleteWorkshopLoading) && onClickDeleteBtn()}}>
                                <img src={teacherDeleteIcon} alt="deleteIcon" className='deleteIconStyle' />
                            </div>
                            :
                            <div className='clearButtonContainer' onClick={onClickClearBtn}>
                                Clear
                            </div>
                        }
                            <button className='saveButtonStyle' type='submit' disabled={workShopLoading||sessionLoading||updateSessionLoading||updateWorkshopLoading}>
                                Save
                            </button>
                    </div>
                </form>
                <ConfirmationPopup
                        open={openConfirmationPopup}
                        message={`Are you sure you want to Delete this session?`}
                        gifData={DeleteGif}
                        yesButtonFunctionality={onYesButtonClick}
                        noButtonFunctionality={onNoButtonClick}
                        messageWidth={'195px'}
                />
            </FormProvider>
        </div>
    </>
  )
}

export default CreateSchedule;